import { type IMutableContext } from 'unleash-proxy-client'

/**
 * The non-customer tenancy value used to target Regard admins (Atlas ADR-0028).
 */
export const REGARD_INTERNAL_CUSTOMER = 'regard'

/** What the suite knows about a session, as the flag context is derived from it. */
export interface FlagIdentity {
  /** Whether the session carries `regard_admin`. Decides the internal bucket, and outranks any claim. */
  isAdmin: boolean
  /** The session's single `customer` claim, or `null` where the token carries none. */
  customerClaim: string | null
  /** The Auth0 user's email, absent in **EMR-embedded mode**, where there is no Auth0 user. */
  email?: string
  /** The Auth0 subject, the fallback identifier where no email is available. */
  subject?: string
}

/**
 * Normalizes a customer name the way the backend's `customer_context` does.
 *
 * Unleash constraint values are exact, so both ends must use the same casing and whitespace.
 */
const normalizeCustomer = (customer: string): string =>
  customer.trim().toLowerCase()

/** The session's tenancy bucket: internal for a Regard admin, its claim otherwise, nothing for neither. */
const resolveCustomer = ({
  isAdmin,
  customerClaim,
}: FlagIdentity): string | undefined => {
  if (isAdmin) {
    return REGARD_INTERNAL_CUSTOMER
  }

  return normalizeCustomer(customerClaim ?? '') || undefined
}

/**
 * Derives this session's Unleash context: who they are, and which tenancy bucket they belong to.
 *
 * `customer` comes from the login claim, not the viewed customer, and remains stable for the session.
 * Missing values are omitted; treating a missing claim as internal could expose unreleased features
 * (Atlas ADR-0028).
 */
export const flagContextFor = (identity: FlagIdentity): IMutableContext => {
  const customer = resolveCustomer(identity)
  const userId = identity.email || identity.subject

  return {
    ...(userId ? { userId } : {}),
    ...(customer ? { properties: { customer } } : {}),
  }
}
